// Rolling seven-day arithmetic — everything the "Past 7 days" overlay states.
//
// Pure functions only: no React, no network, loadable by the probe shim the
// same way burn.js is. Food.js hands in rows and the fitness arrays its burn
// effect already fetched; nothing here fetches anything.
//
// THE RULE THAT MATTERS MOST. A day with no food logged is NOT a zero-calorie
// day. Summing it as zero manufactures a phantom deficit of roughly a full
// day's burn, and it biases in the flattering direction — exactly the error
// that would fool the reader. Unlogged days contribute NOTHING to intake, to
// the deficit total, or to the rate's day count, and any unlogged day in the
// window makes the total a FLOOR the caller must mark.
//
// Per day, three states, the same shape CASE 26 pins:
//   complete   every row disclosed calories
//   partial    some row withheld -> the day's intake is a floor and the day
//              carries the Food-partial mark
//   unlogged   no rows at all    -> contributes nothing anywhere

import { totalNutrients, shiftDay } from './foodLog';
import { fromDateOnly } from '../api/figApi';
import { computeBurn, localDateKey } from './burn';

// A pound of fat, in calories. A unit conversion, not a recommendation.
export const CAL_PER_LB = 3500;

// The observed line needs this many weigh-ins INSIDE the window before it says
// a number. Fewer than four readings across seven days is two points and some
// water weight; the overlay renders an em dash and says so instead.
export const MIN_WEIGHINS_FOR_OBSERVED = 4;

// The rate needs this many countable days for the same reason. Two logged days
// extrapolated to a week is the same class of error as an observed change from
// two readings, and this view refuses both the same way.
export const MIN_DAYS_FOR_RATE = 4;

// Rows -> the day's classification. Calorie totalling is totalNutrients' —
// this reads its Calories entry rather than re-summing, so the overlay and the
// day view cannot disagree about what a day ate.
export function classifyDay(rows) {
  if (rows.length === 0) return { state: 'unlogged', intake: null, undisclosed: 0 };
  const { value, undisclosed } = totalNutrients(rows).Calories;
  return { state: undisclosed > 0 ? 'partial' : 'complete', intake: value, undisclosed };
}

// Range rows -> { dayKey: rows[] } for exactly the given keys. A row whose
// Date falls outside the keys is dropped rather than invented a bucket.
export function groupRowsByDay(rows, dayKeys) {
  const byDay = {};
  for (const key of dayKeys) byDay[key] = [];
  for (const row of rows) {
    const key = fromDateOnly(row.Date);
    if (key !== null && byDay[key]) byDay[key].push(row);
  }
  return byDay;
}

// The window, summarized. `weighIns`, `workouts` and `walks` are the same
// unfiltered arrays the day view's burn effect fetched; computeBurn does its
// own day-matching per key. `walks` defaults to empty for the same reason
// computeBurn's does.
//
// A day contributes to the deficit total and the day count only when BOTH of
// its numbers exist — intake logged and a weigh-in on or before it. Burn and
// swim total over every day that has a burn, logged or not: the swimming
// happened whether or not the food was written down.
export function summarizeWindow({ dayKeys, rowsByDay, weighIns, workouts, walks = [] }) {
  const days = dayKeys.map(dayKey => {
    const cls = classifyDay(rowsByDay[dayKey] ?? []);
    const burn = computeBurn({ weighIns, workouts, walks, dayKey });
    const counted = cls.intake !== null && burn !== null;
    return {
      dayKey,
      state: cls.state,
      intake: cls.intake,
      undisclosed: cls.undisclosed,
      burn,
      // Signed as the day cells display it: intake minus burn, negative when
      // under. Null when either side is unknown — never a zero.
      net: counted ? cls.intake - burn.burnCal : null,
      counted,
    };
  });

  // EVERY total spans the SAME day set: the countable days. A day with no
  // intake contributes neither its intake nor its burn — summing its burn but
  // not its (unknown) intake presented a pair that invited subtraction, and
  // the difference flattered by roughly a full day's burn per missing day.
  // deficitTotal is DEFINED as burnTotal − intakeTotal, so the identity the
  // probe asserts cannot drift; it is not a third accumulation.
  let intakeTotal = null;
  let burnTotal = null;
  let undisclosedRows = 0;
  let burnSwim = 0;
  let burnWalk = 0;
  let swimMeters = 0;
  let daysCounted = 0;
  let loggedCount = 0;
  let unloggedCount = 0;

  for (const d of days) {
    if (d.state !== 'unlogged') loggedCount += 1; else unloggedCount += 1;
    if (!d.counted) continue;
    daysCounted += 1;
    intakeTotal = intakeTotal === null ? d.intake : intakeTotal + d.intake;
    burnTotal = burnTotal === null ? d.burn.burnCal : burnTotal + d.burn.burnCal;
    undisclosedRows += d.undisclosed;
    burnSwim += d.burn.swimCal;
    burnWalk += d.burn.walkCal;
    swimMeters += d.burn.swimMeters;
  }

  return {
    days,
    deficitTotal: burnTotal === null ? null : burnTotal - intakeTotal,
    // The FLOOR mark. Driven by unlogged days — the missing days are the
    // reason the total understates the week — and rendered with the existing
    // Food-partial superscript, never a second marker vocabulary.
    deficitIsFloor: unloggedCount > 0,
    daysCounted,
    loggedCount,
    unloggedCount,
    intakeTotal,           // counted days only; null when none could be counted
    undisclosedRows,       // withheld rows WITHIN the counted days
    burnTotal,             // the same counted days, never a wider set
    // Base is what remains after EVERY activity term, so a walking week
    // cannot inflate the number labelled "base" on screen.
    burnBase: burnTotal === null ? null : burnTotal - burnSwim - burnWalk,
    burnSwim,
    burnWalk,
    swimMeters,
  };
}

// deficit -> pounds per week at this pace: deficit / 3500 * 7 / days-counted.
// Null below MIN_DAYS_FOR_RATE countable days — the arithmetic would be
// right, but a weekly rate from two days of logging is not a fast or slow
// reading, it is no reading. The overlay says "not enough days".
export function weeklyRateLb(deficitTotal, daysCounted) {
  if (deficitTotal === null || daysCounted < MIN_DAYS_FOR_RATE) return null;
  return (deficitTotal / CAL_PER_LB) * (7 / daysCounted);
}

// Predicted weight CHANGE over the window, signed like a scale reads: a
// deficit predicts a negative change. deficit / 3500, negated so PREDICTED
// and OBSERVED are the same kind of number and the gap between them is a
// subtraction, not a sign convention.
export function predictedChangeLb(deficitTotal) {
  if (deficitTotal === null) return null;
  return -(deficitTotal / CAL_PER_LB);
}

// Mean of every reading in the seven days ending at dayKey, or null when that
// span holds none. An average, never two raw endpoints: daily water swing is
// several pounds and a pound of fat is 3,500 cal, so raw endpoints are noise.
export function rollingAvgWeight(weighIns, dayKey) {
  const startKey = shiftDay(dayKey, -6);
  let sum = 0;
  let n = 0;
  for (const row of weighIns) {
    const lbs = row.WeightLbs;
    if (typeof lbs !== 'number' || !Number.isFinite(lbs)) continue;
    const key = localDateKey(row.WeighDate);
    if (key === null || key < startKey || key > dayKey) continue;
    sum += lbs;
    n += 1;
  }
  return n === 0 ? null : sum / n;
}

// Observed change: rolling average at window end minus the same average at
// window start. Gated on MIN_WEIGHINS_FOR_OBSERVED readings INSIDE the
// window — below that the overlay says "not enough readings" rather than
// computing something misleading.
export function observedChangeLb(weighIns, startKey, endKey) {
  let inWindow = 0;
  for (const row of weighIns) {
    const lbs = row.WeightLbs;
    if (typeof lbs !== 'number' || !Number.isFinite(lbs)) continue;
    const key = localDateKey(row.WeighDate);
    if (key !== null && key >= startKey && key <= endKey) inWindow += 1;
  }
  if (inWindow < MIN_WEIGHINS_FOR_OBSERVED) return null;

  const endAvg = rollingAvgWeight(weighIns, endKey);
  const startAvg = rollingAvgWeight(weighIns, startKey);
  if (endAvg === null || startAvg === null) return null;
  return endAvg - startAvg;
}
